import type { EncounterFeeGate } from '@his/shared';
import type { EncounterClinicalOrder, EncounterLabOrder, EncounterStudy } from '../ConsultationOrders';

export interface EncounterVitals {
  id: string;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulse: number | null;
  respiratoryRate?: number | null;
  temperatureC: number | null;
  spo2: number | null;
  weightKg: number | null;
  heightCm: number | null;
  bmi: number | null;
  painScore?: number | null;
  bloodSugarMgDl?: number | null;
  headCircumferenceCm?: number | null;
  notes?: string | null;
  isAbnormal: boolean;
  isRecheck?: boolean;
  recheckReason?: string | null;
  recordedAt: string;
}

export interface Encounter {
  id: string;
  appointmentId?: string | null;
  status: string;
  context: string;
  chiefComplaint: string | null;
  complaintDurationValue?: number | null;
  complaintDurationUnit?: string | null;
  disposition?: string | null;
  claimFlags?: string[];
  allergiesVerifiedAt?: string | null;
  appointment?: { payerId: string | null; type?: string | null } | null;
  consultationInvoice?: { id: string; number: string; status: string } | null;
  feeGate?: EncounterFeeGate | null;
  followUpValidDays?: number | null;
  startedAt: string;
  followUpDate: string | null;
  addenda: { id: string; body: string; createdAt: string; authorName: string | null }[];
  notes: Record<string, unknown>;
  emrNotNeeded?: string[];
  vitalsNotTakenReason?: string | null;
  recordedForms?: string[];
  clinicalOrders?: (EncounterClinicalOrder & { status?: string })[];
  patient: {
    id: string;
    mrn: string;
    fullName: string;
    sex: string;
    dob: string | null;
    approximateAgeYears: number | null;
    phone?: string | null;
    bloodGroup?: string | null;
    category?: string | null;
    allergyStatus?: string | null;
    birthWeightGrams?: number | null;
    gestationalAgeWeeks?: number | null;
    gestationalAgeDays?: number | null;
    photoUrl?: string | null;
    allergies: { id: string; substance: string; reaction: string | null; severity: string }[];
  };
  doctor: { id: string; name: string; designation: string | null };
  diagnoses: { id: string; icdCode: string; icdTitle: string; isPrimary: boolean; certainty: string; conditionOnset?: string | null }[];
  vitals: EncounterVitals[];
  prescriptions: { id: string; status: string; issuedAt: string; items: { id?: string; drugName: string; dose: string; frequency: string }[] }[];
  labOrders?: EncounterLabOrder[];
  radiologyStudies?: EncounterStudy[];
}

export function noteText(encounter: Pick<Encounter, 'notes'>, key: string): string {
  const value = encounter.notes?.[key];
  return typeof value === 'string' ? value : '';
}
